import { getAuthRefreshV2, getPosSalesAgentAuthRefresh, postAuthInitV2 } from 'data-api'
import isUndefined from 'lodash/isUndefined'
import router from 'next/router'
import { useCallback } from 'react'
import { Cookies } from 'react-cookie'
import {
	COOKIE_NAME_JWT,
	COOKIE_NAME_SALES_AGENT_GROUP,
	COOKIE_NAME_SALES_AGENT_JWT,
	COOKIE_NAME_USER_GROUP,
	PAGE_PATH_LOGIN,
	PAGE_PATH_SALES_AGENT_LOGIN,
} from '../constants'
import { useStore } from '../providers/store'
import { formatGroupCookieName } from '../utils'
import { getNextTenDaysDate } from '../utils/date'
import { useCookiesByGroup } from './useCookiesByGroup'

let currentRun

async function runOnce(generateToken) {
	if (currentRun) {
		return await currentRun
	}

	currentRun = generateToken()
	const result = await currentRun

	currentRun = null
	return result
}

const verifyCookieToken = (token, cookieName) => {
	const cookies = new Cookies()
	const cookieToken = cookies.get(cookieName)

	if (cookieToken && token && token !== cookieToken) {
		// Token changed during another call.
		// Other api calls could have initialized or refreshed auth.
		return cookieToken
	}
}

export const useAuth = (client) => {
	if (!client) throw new Error('Client is required')

	const [, setCookiesByGroup, removeCookiesByGroup] = useCookiesByGroup()
	const { groupId } = useStore()
	const groupCookieNameJwt = formatGroupCookieName(groupId, COOKIE_NAME_JWT)
	const groupCookieNameSalesAgentJwt = formatGroupCookieName(groupId, COOKIE_NAME_JWT)
	const groupCookieNameUserGroup = formatGroupCookieName(groupId, COOKIE_NAME_USER_GROUP)

	const authInit = useCallback(
		async (currentToken) => {
			const cookieToken = verifyCookieToken(currentToken, groupCookieNameJwt)
			if (cookieToken) {
				return { token: cookieToken }
			}

			const { token, sessionData } = await runOnce(async () => {
				const {
					data: { token, sessionData },
				} = await postAuthInitV2({
					client,
				})
				setCookiesByGroup(COOKIE_NAME_JWT, token, {
					expires: getNextTenDaysDate(),
				})
				setCookiesByGroup(COOKIE_NAME_USER_GROUP, sessionData.groupId, {
					expires: getNextTenDaysDate(),
				})
				return { token, sessionData }
			})

			return { token, sessionData }
		},
		[setCookiesByGroup, client, groupCookieNameJwt]
	)

	const authRefresh = useCallback(
		async (currentToken, isSalesAgentRequest) => {
			const cookieToken = verifyCookieToken(
				currentToken,
				isSalesAgentRequest ? groupCookieNameSalesAgentJwt : groupCookieNameJwt
			)
			if (cookieToken) {
				return { token: cookieToken }
			}

			const apiAuthRefresh = isSalesAgentRequest ? getPosSalesAgentAuthRefresh : getAuthRefreshV2

			const { token, sessionData } = await runOnce(async () => {
				const {
					data: { token, sessionData },
				} = await apiAuthRefresh({
					client,
					token: currentToken,
				})
				setCookiesByGroup(isSalesAgentRequest ? COOKIE_NAME_SALES_AGENT_JWT : COOKIE_NAME_JWT, token, {
					expires: getNextTenDaysDate(),
				})
				setCookiesByGroup(
					isSalesAgentRequest ? COOKIE_NAME_SALES_AGENT_GROUP : COOKIE_NAME_USER_GROUP,
					sessionData.groupId,
					{
						expires: getNextTenDaysDate(),
					}
				)
				return { token, sessionData }
			})

			return { token, sessionData }
		},
		[setCookiesByGroup, client, groupCookieNameJwt, groupCookieNameSalesAgentJwt]
	)

	const handleInsufficientAuth = useCallback(
		async (isSalesAgentRequest) => {
			if (isSalesAgentRequest) {
				// clean salesAgentToken
				removeCookiesByGroup(COOKIE_NAME_SALES_AGENT_JWT)
				removeCookiesByGroup(COOKIE_NAME_SALES_AGENT_GROUP)
				router.push(PAGE_PATH_SALES_AGENT_LOGIN)
			} else {
				router.push(PAGE_PATH_LOGIN)
			}
		},
		[removeCookiesByGroup]
	)

	const refreshAuthCookies = useCallback(() => {
		const cookies = new Cookies()
		const token = cookies.get(groupCookieNameJwt)
		const groupId = cookies.get(groupCookieNameUserGroup)

		if (!isUndefined(token)) {
			setCookiesByGroup(COOKIE_NAME_JWT, token, {
				expires: getNextTenDaysDate(),
			})
		}
		if (!isUndefined(groupId)) {
			setCookiesByGroup(COOKIE_NAME_USER_GROUP, groupId, {
				expires: getNextTenDaysDate(),
			})
		}
	}, [groupCookieNameJwt, groupCookieNameUserGroup, setCookiesByGroup])

	return { authInit, authRefresh, handleInsufficientAuth, refreshAuthCookies }
}
